/**
 * Custom useSearchParams hook for KeepAlive compatibility
 *
 * Same rationale as @/hooks/useLocation — react-router-dom's useSearchParams
 * reads the global browser URL, which is wrong for hidden tabs.
 *
 * This hook derives search params from TabLocationContext when available,
 * otherwise falls back to react-router-dom's useSearchParams.
 */

import { useTabLocation } from '@/context/TabLocationContext';
import { useActiveTabContext } from '@/context/ActiveTabContext';
import { useTabsContextSafe } from '@/context/TabsContext';
import { ROOT_PANE_ID } from '@/context/TabsContext/reducer';
import { useMemo, useCallback } from 'react';
import { useSearchParams as useReactRouterSearchParams } from 'react-router-dom';
import type { URLSearchParamsInit } from 'react-router-dom';

/**
 * Get search params for the current tab's location.
 * Returns [URLSearchParams, SetURLSearchParams] — setter only works for the active tab.
 */
export function useSearchParams(
  defaultInit?: URLSearchParamsInit
): ReturnType<typeof useReactRouterSearchParams> {
  const tabLocation = useTabLocation();
  const activeTab = useActiveTabContext();
  const tabsContext = useTabsContextSafe();
  const [routerSearchParams, setRouterSearchParams] = useReactRouterSearchParams(defaultInit as any);

  const tabSearchParams = useMemo(() => {
    if (!tabLocation) return null;
    
    // Merge defaultInit with current tab search params
    const params = new URLSearchParams(defaultInit as any);
    const currentParams = new URLSearchParams(tabLocation.search);
    
    currentParams.forEach((value, key) => {
      params.set(key, value);
    });
    
    return params;
  }, [tabLocation?.search, defaultInit]);

  const setTabSearchParams = useCallback(
    (
      nextInit: URLSearchParamsInit | ((prev: URLSearchParams) => URLSearchParamsInit),
      navigateOptions?: { replace?: boolean; state?: any }
    ) => {
      // Fallback if outside KeepAliveOutlet
      if (!tabLocation || !tabsContext) {
        return setRouterSearchParams(nextInit, navigateOptions);
      }

      // Prevent background tabs from hijacking the current URL
      if (!activeTab.isActive) {
        console.warn(`[KeepAlive] Blocked setSearchParams from background tab: ${tabLocation.key}`);
        return;
      }

      // Calculate new search string
      const prevParams = new URLSearchParams(tabLocation.search);
      const nextParamsInit = typeof nextInit === 'function' ? nextInit(prevParams) : nextInit;
      const nextParams = new URLSearchParams(nextParamsInit as any);
      const searchString = nextParams.toString();
      const search = searchString ? `?${searchString}` : '';
      const isSplitView = tabsContext.state.panes.length > 1;
      const isPaneLocalSearch = isSplitView && Boolean(tabLocation.paneId && tabLocation.paneId !== ROOT_PANE_ID);

      // Navigate within the tab system to keep history in sync
      tabsContext.navigateInTab(tabLocation.pathname, {
        search,
        state: navigateOptions?.state,
        replace: navigateOptions?.replace,
        paneId: tabLocation.paneId,
        syncBrowserUrl: !isPaneLocalSearch,
      });
    },
    [tabLocation, activeTab.isActive, tabsContext, setRouterSearchParams]
  );

  if (tabSearchParams) {
    return [tabSearchParams, setTabSearchParams as ReturnType<typeof useReactRouterSearchParams>[1]];
  }

  return [routerSearchParams, setRouterSearchParams];
}

export default useSearchParams;
